import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Palette, 
  Crown, 
  Sparkles, 
  Check, 
  Lock,
  Star,
  Zap,
  Gift,
  Percent,
  Clock,
  ShoppingBag,
  Ticket as TicketIcon
} from 'lucide-react';
import { Button } from './ui/button';

// Custom Ticket SVG component (fallback)
const TicketSvg = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M2 9a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v0a3 3 0 0 1-3 3v0a3 3 0 0 1 3 3v0a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3v0a3 3 0 0 1 3-3v0a3 3 0 0 1-3-3Z"/>
  </svg>
);

// Premium template definitions
const KUPON_TEMPLATES = [
  {
    id: 'classic',
    name: 'Clásico',
    description: 'Diseño limpio y profesional',
    premium: false,
    colors: { bg: '#ffffff', accent: '#4A7C59', text: '#1a1a1a' },
    icon: TicketIcon,
    preview: 'bg-white border-2 border-gray-200'
  },
  {
    id: 'gradient-sage',
    name: 'Degradado Verde',
    description: 'Elegante con gradiente natural',
    premium: false,
    colors: { bg: 'linear-gradient(135deg, #4A7C59, #2D5A3D)', accent: '#ffffff', text: '#ffffff' },
    icon: Sparkles,
    preview: 'bg-gradient-to-br from-emerald-600 to-emerald-800 text-white'
  },
  {
    id: 'dark-luxury',
    name: 'Lujo Oscuro',
    description: 'Premium y sofisticado',
    premium: true,
    colors: { bg: '#1a1a1a', accent: '#d4af37', text: '#ffffff' },
    icon: Crown,
    preview: 'bg-gray-900 text-white border-2 border-yellow-500'
  },
  {
    id: 'sunset',
    name: 'Atardecer',
    description: 'Cálido y llamativo',
    premium: true,
    colors: { bg: 'linear-gradient(135deg, #ff6b6b, #ffa500)', accent: '#ffffff', text: '#ffffff' },
    icon: Star,
    preview: 'bg-gradient-to-br from-red-400 to-orange-500 text-white'
  },
  {
    id: 'ocean',
    name: 'Océano',
    description: 'Fresco y moderno',
    premium: true,
    colors: { bg: 'linear-gradient(135deg, #0077b6, #00b4d8)', accent: '#ffffff', text: '#ffffff' },
    icon: Zap,
    preview: 'bg-gradient-to-br from-blue-600 to-cyan-500 text-white'
  },
  {
    id: 'minimal-mono',
    name: 'Minimalista',
    description: 'Blanco y negro elegante',
    premium: true,
    colors: { bg: '#f8f8f8', accent: '#000000', text: '#000000' },
    icon: Palette,
    preview: 'bg-gray-100 border-2 border-black'
  },
  {
    id: 'neon-purple',
    name: 'Neón Púrpura',
    description: 'Vibrante y moderno',
    premium: true,
    colors: { bg: 'linear-gradient(135deg, #7c3aed, #a855f7)', accent: '#ffffff', text: '#ffffff' },
    icon: Sparkles,
    preview: 'bg-gradient-to-br from-violet-600 to-purple-500 text-white'
  },
  {
    id: 'gold-premium',
    name: 'Oro Premium',
    description: 'Exclusivo y lujoso',
    premium: true,
    colors: { bg: 'linear-gradient(135deg, #d4af37, #f4e4bc)', accent: '#1a1a1a', text: '#1a1a1a' },
    icon: Crown,
    preview: 'bg-gradient-to-br from-yellow-500 to-yellow-200'
  }
];

const Ticket = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M2 9a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v0a3 3 0 0 1-3 3v0a3 3 0 0 1 3 3v0a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3v0a3 3 0 0 1 3-3v0a3 3 0 0 1-3-3Z"/>
  </svg>
);

export function KuponTemplateSelector({ 
  selectedTemplate, 
  onSelect, 
  ownedTemplates = [], 
  hasUnlimitedAccess = false 
}) {
  const [hoveredTemplate, setHoveredTemplate] = useState(null);

  const canUseTemplate = (template) => {
    if (!template.premium) return true;
    if (hasUnlimitedAccess) return true;
    return ownedTemplates.includes(template.id);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
          <Palette className="w-4 h-4" />
          Diseño del Kupon
        </h3>
        {!hasUnlimitedAccess && (
          <span className="text-xs text-muted-foreground">
            {ownedTemplates.length} plantillas premium
          </span>
        )}
      </div>

      <div className="grid grid-cols-4 gap-3">
        {KUPON_TEMPLATES.map((template) => {
          const isSelected = selectedTemplate === template.id;
          const canUse = canUseTemplate(template);
          const IconComponent = template.icon;

          return (
            <motion.button
              key={template.id}
              type="button"
              onClick={() => canUse && onSelect(template.id)}
              onMouseEnter={() => setHoveredTemplate(template.id)}
              onMouseLeave={() => setHoveredTemplate(null)}
              whileHover={canUse ? { scale: 1.05 } : {}}
              whileTap={canUse ? { scale: 0.95 } : {}}
              className={`relative p-3 rounded-xl border-2 transition-all ${
                isSelected 
                  ? 'border-primary ring-2 ring-primary/20' 
                  : 'border-transparent hover:border-muted'
              } ${!canUse ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
              data-testid={`template-${template.id}`}
            >
              {/* Template Preview */}
              <div className={`w-full aspect-[4/3] rounded-lg ${template.preview} flex items-center justify-center mb-2 relative overflow-hidden`}>
                <IconComponent className="w-6 h-6 opacity-80" />
                
                {/* Premium Badge */}
                {template.premium && (
                  <div className="absolute top-1 right-1">
                    {canUse ? (
                      <Crown className="w-3 h-3 text-yellow-500" />
                    ) : (
                      <Lock className="w-3 h-3 text-gray-400" />
                    )}
                  </div>
                )}

                {/* Selected Check */}
                {isSelected && (
                  <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                    <div className="bg-primary rounded-full p-1">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  </div>
                )}
              </div>

              {/* Template Name */}
              <p className="text-xs font-medium text-center truncate">{template.name}</p>
            </motion.button>
          );
        })}
      </div>

      {/* Tooltip on hover */}
      <AnimatePresence>
        {hoveredTemplate && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            className="bg-popover text-popover-foreground text-xs p-2 rounded-lg shadow-lg border"
          >
            {KUPON_TEMPLATES.find(t => t.id === hoveredTemplate)?.description}
            {KUPON_TEMPLATES.find(t => t.id === hoveredTemplate)?.premium && 
             !canUseTemplate(KUPON_TEMPLATES.find(t => t.id === hoveredTemplate)) && (
              <span className="text-amber-500 ml-1">• Requiere compra</span>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Preview component for kupon with template
export function KuponTemplatePreview({ template, kuponData }) {
  const templateConfig = KUPON_TEMPLATES.find(t => t.id === template) || KUPON_TEMPLATES[0];
  
  return (
    <div 
      className={`rounded-xl p-4 ${templateConfig.preview} transition-all`}
      style={{ minHeight: '120px' }}
    >
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <h4 className="font-bold text-lg">{kuponData.title || 'Título del Kupon'}</h4>
          <p className="text-sm opacity-80 line-clamp-2">{kuponData.description || 'Descripción...'}</p>
        </div>
        <div className="text-right">
          {kuponData.discountedPrice && kuponData.originalPrice && (
            <>
              <p className="text-xs line-through opacity-60">{kuponData.originalPrice}€</p>
              <p className="text-xl font-bold">{kuponData.discountedPrice}€</p>
              <p className="text-xs font-semibold bg-black/20 px-2 py-0.5 rounded inline-block">
                -{Math.round((1 - kuponData.discountedPrice / kuponData.originalPrice) * 100)}%
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export { KUPON_TEMPLATES };
export default KuponTemplateSelector;
